/* ==========================================================================
   EdgeRunner 2.0 listicle — progressive enhancement
   --------------------------------------------------------------------------
   Nothing here is required for the page to work. Every link is a plain
   anchor, all copy and imagery render without JavaScript, and the sticky
   bar is the only element this file controls.

   1. Sticky CTA: shows once the hero CTA has scrolled out of view, hides
      again when the final CTA is on screen, and stays down while the
      consent bar occupies the bottom of the viewport.
   2. Optional custom analytics through Shopify's own layer only. No pixel
      is installed, no standard event is duplicated, and a failure here can
      never block a navigation.

   Idempotent and re-runs on shopify:section:load for the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  // Each section tags the stylesheet and this script, so the file can be
  // requested once but evaluated several times. Bind everything only once;
  // the shopify:section:load listener below covers sections added later.
  if (window.__erListicle) return;
  window.__erListicle = true;

  var CONSENT_SELECTOR = 'privacy-bar:not([hidden]), .privacy-bar:not([hidden])';

  /* ---------------------------------------------------------------- analytics */

  function publish(name, payload) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        analytics.publish(name, payload || {});
      }
    } catch (error) {
      /* Telemetry must never interfere with the page. */
    }
  }

  function analyticsEnabled(root) {
    return !!(root || document).querySelector('[data-er-analytics="true"]');
  }

  function initPageView() {
    if (document.documentElement.hasAttribute('data-er-view-sent')) return;
    if (!analyticsEnabled(document)) return;
    document.documentElement.setAttribute('data-er-view-sent', 'true');
    publish('edgerunner_listicle_view', {});
  }

  function initCtaTracking(root) {
    if (!analyticsEnabled(document)) return;
    var links = (root || document).querySelectorAll('[data-er-cta]:not([data-er-cta-bound])');
    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute('data-er-cta-bound', '');
      link.addEventListener('click', function () {
        publish('edgerunner_listicle_cta_click', { placement: link.getAttribute('data-er-cta') || 'unknown' });
      });
    });
  }

  function initReasonTracking(root) {
    if (!analyticsEnabled(document)) return;
    if (!('IntersectionObserver' in window)) return;

    var reasons = (root || document).querySelectorAll('[data-er-reason]:not([data-er-reason-bound])');
    if (!reasons.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var reason = entry.target.getAttribute('data-er-reason');
          var key = 'er_reason_' + reason;
          observer.unobserve(entry.target);
          try {
            if (window.sessionStorage && window.sessionStorage.getItem(key)) return;
            if (window.sessionStorage) window.sessionStorage.setItem(key, '1');
          } catch (error) {
            /* Private mode or blocked storage: fall through and still report once per load. */
          }
          publish('edgerunner_listicle_reason_view', { reason: reason });
        });
      },
      { threshold: 0.4 }
    );

    Array.prototype.forEach.call(reasons, function (reason) {
      reason.setAttribute('data-er-reason-bound', '');
      observer.observe(reason);
    });
  }

  /* ------------------------------------------------------------- sticky bar */

  function initSticky(root) {
    var bars = (root || document).querySelectorAll('.er-sticky:not([data-er-sticky-bound])');
    if (!bars.length) return;

    Array.prototype.forEach.call(bars, function (bar) {
      bar.setAttribute('data-er-sticky-bound', '');

      var finalCta = document.querySelector('[data-er-anchor="final-cta"]');

      // How far down the page the bar appears, as a percentage of the
      // scrollable distance. Set per section in the theme editor.
      var showAt = parseFloat(bar.getAttribute('data-show-at'));
      if (isNaN(showAt)) showAt = 25;

      var finalVisible = false;

      // Cached so the scroll handler never reads scrollHeight, which would
      // force a layout on every event. Re-measured on resize and on load,
      // when late images have settled the page height.
      var scrollMax = 0;

      function measure() {
        scrollMax = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      }

      function scrolledEnough() {
        if (scrollMax <= 0) return false;
        return (window.pageYOffset / scrollMax) * 100 >= showAt;
      }

      function consentVisible() {
        var bannerEl = document.querySelector(CONSENT_SELECTOR);
        if (!bannerEl) return false;
        var styles = window.getComputedStyle(bannerEl);
        return styles.display !== 'none' && styles.visibility !== 'hidden' && parseFloat(styles.opacity || '1') > 0.05;
      }

      function sync() {
        var show = scrolledEnough() && !finalVisible && !consentVisible();
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      // The final invitation already carries a button, so the bar stands down
      // over it rather than doubling up.
      if (finalCta && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.target === finalCta) finalVisible = entry.isIntersecting;
            });
            sync();
          },
          { threshold: 0 }
        );
        observer.observe(finalCta);
      }

      // Deliberately not throttled through requestAnimationFrame: rAF does not
      // run while the tab is hidden, so a "already queued" latch would stick on
      // and kill scroll handling for good once the visitor came back. sync() is
      // a couple of property reads, and the consent lookup is short-circuited
      // until the threshold is actually crossed.
      window.addEventListener('scroll', sync, { passive: true });

      window.addEventListener(
        'resize',
        function () {
          measure();
          sync();
        },
        { passive: true }
      );

      window.addEventListener('load', function () {
        measure();
        sync();
      });

      // The consent bar appears and disappears outside our control, so recheck
      // whenever the overlay group mutates.
      if ('MutationObserver' in window) {
        var consentWatcher = new MutationObserver(sync);
        consentWatcher.observe(document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'style', 'class'],
        });
      }

      measure();
      sync();
    });
  }

  /* ------------------------------------------------------------------ video */

  function initVideos(root) {
    // Only silent looping videos are handled here. A video set to show controls
    // is left entirely to the visitor.
    var videos = (root || document).querySelectorAll('video[data-er-video]:not([data-er-video-bound])');
    if (!videos.length) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function play(video) {
      video.muted = true;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }

    // With reduced motion, or without an observer, leave the poster showing.
    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(videos, function (video) {
        video.setAttribute('data-er-video-bound', '');
        video.setAttribute('controls', '');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            if (video.preload === 'none') video.preload = 'auto';
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    Array.prototype.forEach.call(videos, function (video) {
      video.setAttribute('data-er-video-bound', '');
      observer.observe(video);
    });
  }

  /* -------------------------------------------------------------- bootstrap */

  function init(root) {
    initPageView();
    initSticky(root);
    initCtaTracking(root);
    initReasonTracking(root);
    initVideos(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
